import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api, { money, API } from "@/lib/api";
import { StatCard } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Loader2, Download, DollarSign, Repeat, TrendingUp } from "lucide-react";

const CATS = [
  { key: "subscription", label: "Subscription", color: "bg-blue-500" },
  { key: "job", label: "Job Revenue", color: "bg-emerald-500" },
  { key: "turnover", label: "Turnover", color: "bg-purple-500" },
  { key: "inspection", label: "Inspection", color: "bg-amber-500" },
  { key: "recurring", label: "Recurring Maint.", color: "bg-teal-500" },
];

export default function Reports() {
  const { user } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/reports/revenue").then(({ data }) => setD(data)).catch(() => setD(false)); }, []);

  const download = async (path, name) => {
    const { data } = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout nav={getNav(user.role)} title="Reporting & Exports">
      {!d ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> : (
        <div className="space-y-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard testid="rep-mrr" label="MRR" value={money(d.mrr)} icon={Repeat} accent="text-blue-600" sub={`${d.active_subscriptions} active subscriptions`} />
            <StatCard testid="rep-arr" label="ARR (projected)" value={money(d.arr)} icon={TrendingUp} accent="text-indigo-600" />
            <StatCard testid="rep-total" label="Total Revenue" value={money(d.total_revenue)} icon={DollarSign} accent="text-green-600" />
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-head font-semibold text-slate-900 mb-4">Revenue by Category</h3>
            <div className="space-y-3">
              {CATS.map((c) => {
                const v = d.categories[c.key] || 0;
                const pct = d.total_revenue ? (v / d.total_revenue * 100) : 0;
                return (
                  <div key={c.key} data-testid={`revcat-${c.key}`}>
                    <div className="flex justify-between text-sm mb-1"><span className="text-slate-600">{c.label}</span><span className="font-semibold text-slate-900">{money(v)}</span></div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${c.color}`} style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-head font-semibold text-slate-900 mb-4">Exports (CSV)</h3>
            <div className="flex flex-wrap gap-3">
              <Button data-testid="export-wo" variant="outline" onClick={() => download("/reports/work-orders.csv", "work_orders.csv")}><Download className="w-4 h-4 mr-1" /> Work Orders</Button>
              <Button data-testid="export-invoices" variant="outline" onClick={() => download("/reports/invoices.csv", "invoices.csv")}><Download className="w-4 h-4 mr-1" /> Invoices</Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
